export function ArchitectureFlow() {
  return (
    <section className="relative py-28 px-6 lg:px-8 bg-sg border-y border-line">
      {/* Visual connector line */}
      <div className="sc" aria-hidden="true">
        <svg width="2" height="24" viewBox="0 0 2 24">
          <line x1="1" y1="0" x2="1" y2="24" className="sc-line" stroke="#2563EB" strokeOpacity="0.3" strokeWidth="1.5" />
        </svg>
        <span className="sc-node" />
      </div>

      <div className="max-w-3xl mx-auto text-center mb-16">
        <h2 className="text-[32px] sm:text-[40px] font-extrabold tracking-tight text-ink leading-[1.1]">
          A user clicks Accept.<br />
          Seven seconds later, a compliance certificate exists.
        </h2>
        <p className="mt-5 text-[16px] text-sub leading-relaxed">
          Every layer between those two moments belongs to Consentry.
        </p>
      </div>

      <div className="max-w-sm mx-auto flex flex-col items-center">
        {/* Node 1 */}
        <div className="flow-node w-full relative">
          <div className="bg-card border border-line rounded-xl2 px-5 py-3.5 flex items-center gap-3 shadow-card">
            <svg className="flow-glow w-4 h-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div>
              <div className="text-[13.5px] font-semibold text-ink">Your Application</div>
              <div className="mono text-[11px] text-muted mt-0.5">User clicks Accept</div>
            </div>
          </div>
          <span className="flow-dot" style={{ animationDelay: '0s' }} />
        </div>

        {/* Node 2 */}
        <div className="flow-node w-full relative mt-9">
          <div className="bg-card border border-line rounded-xl2 px-5 py-3.5 flex items-center gap-3 shadow-card">
            <svg className="flow-glow w-4 h-4 text-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <div>
              <div className="text-[13.5px] font-semibold text-ink">SDK</div>
              <div className="mono text-[11px] text-muted mt-0.5">Captures session context + notice hash</div>
            </div>
          </div>
          <span className="flow-dot" style={{ animationDelay: '0.4s' }} />
        </div>

        {/* Node 3 */}
        <div className="flow-node w-full relative mt-9">
          <div className="bg-card border border-line rounded-xl2 px-5 py-3.5 flex items-center gap-3 shadow-card">
            <svg className="flow-glow w-4 h-4 text-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            <div>
              <div className="text-[13.5px] font-semibold text-ink">Consent Widget</div>
              <div className="mono text-[11px] text-muted mt-0.5">Renders notice, collects signal</div>
            </div>
          </div>
          <span className="flow-dot" style={{ animationDelay: '0.8s' }} />
        </div>

        {/* Node 4 — API Core */}
        <div className="flow-node w-full relative mt-9">
          <div className="bg-ink rounded-xl2 px-5 py-3.5 flex items-center gap-3 shadow-premium">
            <svg className="flow-glow w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
            <div>
              <div className="text-[13.5px] font-semibold text-white">Consent API</div>
              <div className="mono text-[11px] text-[#6B7280] mt-0.5">POST /v1/consent/record</div>
            </div>
          </div>
          <span className="flow-dot" style={{ animationDelay: '1.2s' }} />
        </div>

        {/* Node 5 */}
        <div className="flow-node w-full relative mt-9">
          <div className="bg-card border border-line rounded-xl2 px-5 py-3.5 flex items-center gap-3 shadow-card">
            <svg className="flow-glow w-4 h-4 text-emerald" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div>
              <div className="text-[13.5px] font-semibold text-ink">Audit Log</div>
              <div className="mono text-[11px] text-muted mt-0.5">Append-only · immutable · timestamped</div>
            </div>
          </div>
          <span className="flow-dot" style={{ animationDelay: '1.6s' }} />
        </div>

        {/* Node 6 — Final Outcome */}
        <div className="w-full relative mt-9">
          <div className="bg-card border-2 border-emerald/30 rounded-xl2 px-5 py-3.5 flex items-center gap-3 shadow-card">
            <svg className="flow-glow w-4 h-4 text-emerald" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <div>
              <div className="text-[13.5px] font-semibold text-ink">Certificate Issued</div>
              <div className="mono text-[11px] text-muted mt-0.5">CERT-2026-0091 · signed · exportable</div>
            </div>
            <span className="ml-auto mono text-[10px] text-emerald border border-emerald/25 bg-[#ECFDF5] dark:bg-emerald-950/40 px-2 py-1 rounded-full">
              ~7s
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
